import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  formatReferralCode,
  resolveReferralDriver,
  setPendingReferralCode,
  ReferralDriver,
} from "../../services/referral";
import { TOKEN } from "../../constants/localStoreKeys";
import { styles } from "./styles";

/**
 * Landing page for a driver's referral QR (`/d/<code>`).
 *
 * Reached from a phone camera on the kerbside, by someone who almost certainly
 * has no Beam account. The page's whole job is to turn that scan into a paired
 * passenger without ever leaving the browser: show who the driver is, hand the
 * visitor into signup, and let the code survive that round trip.
 *
 * The code is stashed before we navigate away — the register and verify screens
 * are shared with every other signup and know nothing about referrals, so the
 * only thing tying the two halves together is that stored value.
 */
const DriverReferralLanding: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const history = useHistory();
  const [driver, setDriver] = useState<ReferralDriver | null>(null);
  const [loading, setLoading] = useState(true);

  const displayCode = formatReferralCode(code || "");

  useEffect(() => {
    if (!code) {
      setLoading(false);
      return;
    }
    // Stashed immediately, not on the CTA: a visitor who wanders off to log in
    // by another route should still end up paired.
    setPendingReferralCode(code);
    let cancelled = false;
    resolveReferralDriver(code)
      .then((result) => {
        if (cancelled) return;
        setDriver(result);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  // Already signed in: nothing to onboard, go straight to pairing.
  useEffect(() => {
    if (localStorage.getItem(TOKEN)) history.replace("/referral/done");
  }, [history]);

  const driverName = driver?.name?.trim();

  if (!code) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>Link not recognised</h1>
          <p style={styles.body}>Ask your driver to show you their code again.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {driver?.profileImage ? (
          <img src={driver.profileImage} alt="" style={styles.avatar} />
        ) : (
          <div style={styles.avatarFallback}>
            <span style={styles.avatarInitial}>
              {(driverName || "B").charAt(0).toUpperCase()}
            </span>
          </div>
        )}

        <h1 style={styles.title}>
          {loading
            ? "Checking the code…"
            : driverName
            ? `Ride with ${driverName}`
            : "Ride with your driver"}
        </h1>

        <p style={styles.body}>
          {driverName
            ? `Create your account and ${driverName} becomes your preferred driver — your ride requests go to them first.`
            : "Create your account and this driver becomes your preferred driver — your ride requests go to them first."}
        </p>

        <div style={styles.codeBox}>
          <span style={styles.codeLabel}>Driver code</span>
          <span style={styles.code}>{displayCode}</span>
        </div>

        <button
          type="button"
          style={styles.primaryBtn}
          onClick={() => history.push("/auth/register")}
        >
          Create account
        </button>

        <button
          type="button"
          style={styles.secondaryBtn}
          onClick={() => history.push("/auth/login")}
        >
          I already have an account
        </button>

        {!loading && !driverName && (
          // Resolution failing is not fatal: the code is still stored, and the
          // dispatch service validates it again at bind time.
          <p style={styles.footnote}>
            We couldn’t load this driver’s details, but your code is saved —
            continue and we’ll pair you after sign-in.
          </p>
        )}
      </div>
    </div>
  );
};

export default DriverReferralLanding;
